/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import type Mithril from 'mithril';
import RatingUsersState from '../states/RatingUsersState';

/** A concrete rating level, or the pseudo-level covering reviews that have a text body. */
export type RatingFilter = number | 'isTextReview';

// Optional, as core's own modals declare their attrs, to stay assignable to `UnsafeModalClass`.
export interface RatingUsersListModalAttrs extends IInternalModalAttrs {
	/** The literary work whose raters should be listed. */
	work?: string;
	rating?: RatingFilter;
}

export default class RatingUsersListModal<CustomAttrs extends RatingUsersListModalAttrs = RatingUsersListModalAttrs> extends Modal<CustomAttrs> {
	raters!: RatingUsersState;

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		const { work, rating } = this.attrs;

		this.raters = new RatingUsersState({ filter: { work, rating } });

		this.raters.refresh();
	}

	className() {
		return 'RatingUsersListModal Modal--small';
	}

	title() {
		let { rating } = this.attrs;

		if (rating === 'isTextReview') {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.rating_users_modal.title_text_review');
		}

		return app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.rating_users_modal.title', {
			rating,
			ratingValue: (
				<span>
					<br />
					{rating}
				</span>
			),
		});
	}

	content() {
		const state = this.raters;

		let loading: Mithril.Children;

		if (state.isInitialLoading() || state.isLoadingNext()) {
			loading = <LoadingIndicator />;
		} else if (state.hasNext()) {
			loading = (
				<Button className="Button" onclick={state.loadNext.bind(state)}>
					{app.translator.trans('core.forum.discussion_list.load_more_button')}
				</Button>
			);
		}

		return [
			<ul className="RatingUsersListModal-users">
				{state.getPages().map((pg) => {
					return pg.items.map((u) => (
						<li className="RatingUsersListModal-user">
							<Link href={app.route.user(u)}>
								{avatar(u)} {username(u)}
							</Link>
						</li>
					));
				})}
			</ul>,
			loading,
		];
	}
}
